import { CatalogItem, CatalogItemSimplified } from "common/types/catalog-types";
import { CatalogicalItem } from "common/types/catalogical-type";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import { CatalogService } from "lib/services/catalog-service";
import { RecommendationsService, transformTaxonomyIdForRecs } from "lib/services/recommendations-service";
import { SearchExperienceService } from "lib/services/shop-search-service";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import { useEffect, useState } from "react";

interface UseRecommendationsProps {
  totalItems: number;
  enabled?: boolean;
}

interface UseRecommendationsResult {
  itemsData: CatalogItemSimplified[] | CatalogicalItem[] | null;
  isLoading: boolean;
  error: Error | null;
  sectionTitle: string;
}

export const useRecommendations = ({
  totalItems,
  enabled = true,
}: UseRecommendationsProps): UseRecommendationsResult => {
  const [itemsData, setItemsData] = useState<CatalogItemSimplified[] | CatalogicalItem[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [sectionTitle, setSectionTitle] = useState<string>("emptyText");

  const toggleSearchExperience = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_SEARCH_EXPERIENCE);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchRecommendations = async () => {
      try {
        setIsLoading(true);

        const buffer = 4;
        const fetchCount = totalItems + buffer;
        const recService = new RecommendationsService();
        const response = await recService.getRecommendations({ count: fetchCount });

        const recommendations = response.recommendations;
        setSectionTitle(response.areDefaultRecommendations ? "home.popularChoicesAmazon" : "home.popularChoicesRole");

        if (toggleSearchExperience) {
          const searchService = new SearchExperienceService("software");

          let result;
          try {
            const transformedTxIds = recommendations.map((item) => item.taxonomy_id);
            result = await searchService.getItems<CatalogicalItem>({
              filter: JSON.stringify({ taxonomyId: transformedTxIds }),
              searchQuery: "",
              bypass: true,
              simplifiedResults: true,
            });
          } catch (err) {
            console.warn("Failed to fetch item via SearchExperienceService", err);
            return null;
          }

          if (!result || !result.data) {
            return null;
          }

          const catalogicalItemsMap = new Map(result.data.map((item) => [item.taxonomyId, item]));

          const sortedItems: CatalogicalItem[] = [];
          for (const ref of recommendations) {
            const transformedId = ref.taxonomy_id.replace("atn::software/", "atn::software/");
            const match = catalogicalItemsMap.get(transformedId);
            if (match) {
              sortedItems.push(match);

              if (sortedItems.length === totalItems) {
                break;
              }
            }
          }

          setItemsData(sortedItems.slice(0, totalItems));
        } else {
          const catalogService = new CatalogService();

          const itemPromises = recommendations.map(async (rec) => {
            try {
              const itemTaxId = ItemTaxonomyId.Parse(rec.taxonomy_id);
              const resp = await catalogService.GetItems(itemTaxId, undefined, 1);
              return resp.items?.[0] ?? null;
            } catch (err) {
              console.warn("Failed to fetch item via legacy catalog", err);
              return null;
            }
          });

          const rawResults = await Promise.all(itemPromises);
          const validItems = rawResults.filter((it): it is CatalogItem => it !== null);
          setItemsData(validItems.slice(0, totalItems));
        }
      } catch (err) {
        console.error("Error loading recommendations", err);
        setError(err instanceof Error ? err : new Error("Failed to load recommendations"));
        /**
         * Fallback for recommendation loading failure:
         * Show "Popular Choices in Your Team" instead of an empty space.
         * This maintains a better user experience when the recommendations
         * endpoint fails.
         */
        setSectionTitle("home.popularChoicesRole");
        setItemsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [totalItems]);

  return {
    itemsData,
    isLoading,
    error,
    sectionTitle,
  };
};
